<template>
    <div class="form-group datepicker js-form-validation__group"
         :class="className"
         ref="datepicker"
         data-datepicker-append-alt-time="true"
         :data-datepicker-min-date="minDate"
         :data-datepricker-max-date="maxDate"
         :data-datepicker-default-date="defaultDate">

        <div class="position-relative">
            <FormLabel :label="label" :id="id" :required="required" :class="labelClassName"></FormLabel>
            <input :required="required" :id="id" type="text" class="form-control"
                   :class="{'is-invalid': errors.length}"
                   ref="datepickerInput"
                   :placeholder="placeholder"
                   :name="name + 'input'"
                   :disabled="disabled">
            <span class="icon icon-calendar datepicker__icon" aria-hidden="true"></span>
        </div>

        <input v-model="model"
               :id="id + 'alt'"
               :name="name"
               :disabled="disabled"
               type="hidden"
               readonly
               ref="datepickerAltInput"
        >

        <input v-if="mode === 'range'"
               v-model="modelTo"
               :id="id + 'altTo'"
               :name="name + 'To'"
               :disabled="disabled"
               type="hidden"
               readonly
               ref="datepickerAltToInput"
        >

        <FormErrors :errors="errors"></FormErrors>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, defineEmits, onUpdated, defineExpose, watch } from 'vue';
import FormLabel from '../form-elements/includes/FormLabel.vue';
import FormErrors from "../form-elements/includes/FormErrors.vue";
import * as flatpickr from "flatpickr";
import { ISOStringToDate, dateToISOString, localDateToUTCDate } from "@elements/date-utils";
import { on, closest } from "@elements/dom-utils";

const props = defineProps({
    id: {
        type: String,
        required: true
    },
    className: {
        type: String,
        required: false
    },
    type: {
        type: String,
        default: 'text'
    },
    color: {
        type: String, default:
            'default'
    },
    required: {
        type: Boolean,
        default: false
    },
    label: String,
    name: String,
    size: String,
    placeholder: String,
    modelValue: {
        type: String,
        default: ""
    },
    modelToValue: {
        type: String,
        default: ""
    },
    loading: Boolean,
    errors: {
        type: Array,
        default: []
    },
    minDate: {
        type: String,
        default: 'today'
    },
    maxDate: {
        type: String,
        default: new Date().fp_incr(14)
    },
    defaultDate: {
        type: String,
        default: ''
    },
    appendAltTime: {
        type: Boolean,
        default: true
    },
    mode: {
        type: String,
        default: 'single'
    },
    disable: {
      type: Array,
      default: []
    },
    labelClassName: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
});

const emit = defineEmits(['changeValue', 'update:modelValue', 'update:modelToValue']);

const datepicker = ref(null);
const datepickerInput = ref(null);
const datepickerAltInput = ref(null);
const datepickerAltToInput = ref(null);

const onChangeValue = (e) => {
  emit('changeValue', e);
};

// Life Cycle Hooks

onMounted(() => {
  options.locale =  _config.local || _config.lang;
  options.formatDate = dateObj => dateObj.toLocaleDateString(_config.lang, {day: '2-digit', month: '2-digit', year: 'numeric'});

  loadLocales().then(function () {

        datepicker.value = datepickerInput.value.flatpickr(options);

        if(datepickerAltInput.value.value) {
            let initialDate = new Date(datepickerAltInput.value.value);

            if (props.mode === 'range') {
                let initialDateTo = datepickerAltToInput.value.value ? new Date(datepickerAltToInput.value.value) : new Date(datepickerAltInput.value.value);
                if (datepickerAltToInput.value.value) {
                    initialDateTo = initialDateTo.setDate(initialDateTo.getDate() + 7);
                }

                datepicker.value.setDate([initialDate, initialDateTo]);

                emit("update:modelToValue", dateToISOString(localDateToUTCDate(initialDateTo), options.appendAltTime));
            } else {
                datepicker.value.setDate(initialDate);
            }

            emit("update:modelValue", dateToISOString(localDateToUTCDate(initialDate), options.appendAltTime));
        }
        datepicker.value.setDate(model.value);
        setDisable(props.disable);
    });

  on('focusin', (e) => {
    if(closest('.flatpickr-calendar', e.target) !== null) {
      e.stopImmediatePropagation();
    }
  }, document);
});

onUpdated(() => {
  loadLocales().then(function () {
    //reinit datepicker instance to update value when value prop changes from outside the component
    datepicker.value = datepickerInput.value.flatpickr(options);
    datepicker.value.setDate(model.value);
  });
});

// Functions

const loadLocales = () => {
    let promise;
    let lang = _config.local || _config.lang;
    promise = new Promise(function(resolve, reject) {
        if (lang && lang !== 'en') {
            import(`flatpickr/dist/l10n/${lang}.js`).then(function (data) {
                resolve(data);
            }).catch(function () {
                if (lang !== _config.lang) {
                    import(`flatpickr/dist/l10n/${_config.lang}.js`).then((data) => {resolve(data)});
                } else {
                    resolve();
                }
            });
        } else {
            resolve();
        }
    });

    return promise
}

let options = {
    onChange: function (selectedDates, dateStr, instance) {
        if (selectedDates.length !== 0){
            datepickerAltInput.value.value = dateToISOString(localDateToUTCDate(selectedDates[0]), options.appendAltTime);

            if (datepickerAltToInput.value && options.mode === "range") {
                datepickerAltToInput.value.value = dateToISOString(localDateToUTCDate(selectedDates[1]), options.appendAltTime);
            }
        } else{
            datepickerAltInput.value.value = '';

            // only clear datepickerAltToInput in range mode (otherwise it probably doesn't exist)
            if (datepickerAltToInput.value && options.mode === "range") {
                datepickerAltToInput.value.value = '';
            }
        }

        emit("update:modelValue", datepickerAltInput.value.value);

        if (options.mode === "range") {
            emit("update:modelToValue", datepickerAltToInput.value.value);
        }
    },
    minDate: props.minDate,
    maxDate: props.maxDate,
    appendAltTime: props.appendAltTime,
    mode: props.mode,
    disableMobile: true
};

const setMinDate = (date) => {
    datepicker.value.set('minDate', date);

    if (getDate(datepickerInput.value) && getDate(datepickerInput.value).getTime() < date.getTime()) {
        setDate(date);
    }
}

const setMaxDate = (date) => {
    datepicker.value.set('maxDate', date);

    if (getDate(datepickerInput.value) && getDate(datepickerInput.value).getTime() > date.getTime()) {
        setDate(date);
    }
}

const setEnable = (dates) =>{
    datepicker.value.set('enable', dates);
}

const setDisable = (dates) => {
    datepicker.value.set('disable', dates);
}

const getDate = () => {
    return ISOStringToDate(datepickerAltInput.value.value);
}

const setDate = (date) => {
    datepicker.value.setDate(new Date(date));
    datepicker.value.datepickerInput.value = new Date(date).toLocaleDateString(_config.lang);
    datepickerAltInput.value.value = dateToISOString(localDateToUTCDate(date), options.appendAltTime);
}

const model = computed(() => props.modelValue);

const modelTo = computed(() => props.modelToValue);

</script>